import React from 'react';
import packageJson from '../../../package.json';
import Sheet from '../ui/Sheet';
import { cn } from '../ui/cn';
import { ChevronRight, DownloadCloud, RefreshCw, LogOut, Loader } from '../../icons';
import { SECONDARY_NAV } from './navItems';

const Row = ({ icon: Icon, label, hint, onClick, disabled, danger, trailing, badge }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'w-full flex items-center gap-3 px-4 h-14 text-left transition-colors',
      danger ? 'text-danger hover:bg-danger-soft' : 'text-primary hover:bg-white/5',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    {Icon && <Icon className="w-5 h-5 flex-shrink-0 text-secondary" />}
    <span className="flex-1 min-w-0">
      <span className="block text-[15px] font-medium truncate">{label}</span>
      {hint && <span className="block text-xs text-secondary truncate">{hint}</span>}
    </span>
    {badge}
    {trailing !== undefined ? trailing : <ChevronRight className="w-4 h-4 text-muted" />}
  </button>
);

const Group = ({ children }) => (
  <div className="bg-page border border-line rounded-card overflow-hidden divide-y divide-line">{children}</div>
);

/**
 * Mobile "More" sheet: secondary destinations, bulk actions and the account.
 */
const MoreSheet = ({
  isOpen,
  onClose,
  onNavigate,
  isAdmin,
  userDisplayName,
  userEmail,
  onSignOut,
  onExportCSV,
  onRefetchAll,
  isRefetchingAll,
  lastSyncLabel,
}) => {
  const go = (id) => {
    onNavigate(id);
    onClose();
  };
  const initials = (userDisplayName || 'U').trim().split(/\s+/).map((p) => p.charAt(0)).slice(0, 2).join('').toUpperCase();

  return (
    <Sheet isOpen={isOpen} onClose={onClose} mode="sheet" size="sm" hideHeader aria-label="More" bodyClassName="space-y-3 pt-2">
      {/* Account */}
      <div className="flex items-center gap-3 px-1 py-2">
        <div className="w-12 h-12 rounded-full bg-accent-soft text-accent flex items-center justify-center text-base font-semibold">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-primary truncate">{userDisplayName || 'User'}</div>
          {userEmail && <div className="text-xs text-secondary truncate">{userEmail}</div>}
        </div>
        <button
          type="button"
          onClick={() => {
            onClose();
            onSignOut && onSignOut();
          }}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm text-secondary hover:text-danger hover:bg-white/5"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <Group>
        {SECONDARY_NAV.filter((i) => !i.adminOnly || isAdmin).map((item) => (
          <Row
            key={item.id}
            icon={item.icon}
            label={item.label}
            hint={item.id === 'sync-history' && lastSyncLabel ? lastSyncLabel : undefined}
            badge={
              item.adminOnly ? (
                <span className="px-2 py-0.5 rounded-md bg-accent-soft text-accent text-[11px] font-semibold">Admin</span>
              ) : null
            }
            onClick={() => go(item.id)}
          />
        ))}
      </Group>

      <Group>
        <Row icon={DownloadCloud} label="Export CSV" onClick={() => { onClose(); onExportCSV && onExportCSV(); }} disabled={!onExportCSV} trailing={null} />
        <Row
          icon={isRefetchingAll ? Loader : RefreshCw}
          label={isRefetchingAll ? 'Refetching…' : 'Refetch all properties'}
          onClick={() => { onClose(); onRefetchAll && onRefetchAll(); }}
          disabled={!onRefetchAll || isRefetchingAll}
          trailing={null}
        />
      </Group>

      <p className="text-center text-xs text-muted pt-1">BZ-Tracker v{packageJson.version}</p>
    </Sheet>
  );
};

export default MoreSheet;
